import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import axiosClient from "../../../../api/axiosClient";

import tradingActualDispatchService from "../services/tradingActualDispatchService";
import tradingDispatchOrderService from "../../dispatch_order/services/tradingDispatchOrderService";

const warehouseApi = "/api/ppe/material-flow/warehouse/master/list";

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: "10px",
  padding: "20px",
  marginBottom: "18px",
};

const sectionTitleStyle = {
  margin: "0 0 18px",
  fontSize: "16px",
  fontWeight: 700,
  color: "#1f2937",
};

const labelStyle = {
  display: "block",
  fontSize: "12px",
  fontWeight: 600,
  color: "#475569",
  marginBottom: "6px",
};

const inputStyle = {
  width: "100%",
  padding: "9px 11px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  fontSize: "13px",
  color: "#1f2937",
  background: "#fff",
  boxSizing: "border-box",
};

const tableHeaderStyle = {
  padding: "11px 10px",
  textAlign: "left",
  fontSize: "11px",
  fontWeight: 700,
  color: "#475569",
  background: "#f8fafc",
  borderBottom: "1px solid #e5e7eb",
  whiteSpace: "nowrap",
};

const tableCellStyle = {
  padding: "11px 10px",
  fontSize: "12px",
  color: "#374151",
  borderBottom: "1px solid #edf0f4",
  verticalAlign: "middle",
};

const primaryButtonStyle = {
  padding: "10px 19px",
  border: "none",
  borderRadius: "6px",
  background: "#5145cd",
  color: "#fff",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "10px 16px",
  border: "1px solid #d1d5db",
  borderRadius: "6px",
  background: "#fff",
  color: "#374151",
  fontSize: "13px",
  fontWeight: 600,
  cursor: "pointer",
};

const formatQuantity = (value) => {
  const number = Number(value || 0);
  return number.toLocaleString("en-IN", {
    maximumFractionDigits: 3,
  });
};

const formatDate = (value) => {
  if (!value) return "-";
  return String(value).slice(0, 10);
};

const getResponseData = (response) => {
  return response?.data?.data ?? response?.data ?? {};
};

const getErrorMessage = (error) => {
  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    "Something went wrong."
  );
};

const getWarehouseId = (warehouse) =>
  warehouse?.id ?? warehouse?.warehouse_id;

const getWarehouseName = (warehouse) =>
  warehouse?.warehouse_name ||
  warehouse?.warehouse_code ||
  warehouse?.name ||
  warehouse?.warehouse_code_name ||
  `Warehouse ${getWarehouseId(warehouse)}`;

const ActualDispatchCreatePage = () => {
  const navigate = useNavigate();

  const [dispatchOrders, setDispatchOrders] = useState([]);
  const [selectedDispatchOrderId, setSelectedDispatchOrderId] = useState("");
  const [selectedDispatchOrder, setSelectedDispatchOrder] = useState(null);

  const [dispatchItems, setDispatchItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);

  const [actualDispatchDate, setActualDispatchDate] = useState(
    new Date().toISOString().slice(0, 10)
  );

  const [transporterBpId, setTransporterBpId] = useState("");
  const [transporters, setTransporters] = useState([]);
  const [loadingTransporters, setLoadingTransporters] = useState(false);

  const [vehicleNumber, setVehicleNumber] = useState("");
  const [driverName, setDriverName] = useState("");
  const [driverMobile, setDriverMobile] = useState("");
  const [lrNumber, setLrNumber] = useState("");
  const [lrDate, setLrDate] = useState("");
  const [remarks, setRemarks] = useState("");

  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  const [orderSearch, setOrderSearch] = useState("");

  // ---------------------------------------------------------
  // LOAD APPROVED DISPATCH ORDERS
  // ---------------------------------------------------------

  const loadDispatchOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      setError("");

      const response =
        await tradingDispatchOrderService.getDispatchOrders({
          status_code: "APPROVED",
        });

      const data = getResponseData(response);

      const rows = Array.isArray(data)
        ? data
        : data?.items || data?.dispatch_orders || data?.records || [];

      setDispatchOrders(
        rows.filter(
          (order) =>
            String(order.status_code || "").toUpperCase() === "APPROVED"
        )
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  // ---------------------------------------------------------
  // LOAD WAREHOUSES
  // ---------------------------------------------------------

  const loadWarehouses = useCallback(async () => {
    try {
      setLoadingWarehouses(true);

      const response = await axiosClient.get(warehouseApi);
      const data = getResponseData(response);

      const rows = Array.isArray(data)
        ? data
        : data?.items || data?.warehouses || [];

      setWarehouses(
        rows.filter(
          (warehouse) =>
            warehouse.is_active === undefined ||
            warehouse.is_active === true ||
            warehouse.is_active === 1
        )
      );
    } catch (err) {
      setError(
        `Unable to load warehouses. ${getErrorMessage(err)}`
      );
    } finally {
      setLoadingWarehouses(false);
    }
  }, []);

  // ---------------------------------------------------------
  // LOAD TRANSPORTERS
  // ---------------------------------------------------------

  const loadTransporters = useCallback(async () => {
    try {
      setLoadingTransporters(true);

      const response = await axiosClient.get(
        "/api/ppe/transport/transporters/search"
      );

      const data = getResponseData(response);

      const rows = Array.isArray(data)
        ? data
        : data?.items || data?.transporters || [];

      setTransporters(rows);
    } catch (err) {
      console.error("Unable to load transporters:", err);
    } finally {
      setLoadingTransporters(false);
    }
  }, []);

  useEffect(() => {
    loadDispatchOrders();
    loadWarehouses();
    loadTransporters();
  }, [loadDispatchOrders, loadWarehouses, loadTransporters]);

  // ---------------------------------------------------------
  // SELECT DISPATCH ORDER AND LOAD AVAILABLE ITEMS
  // ---------------------------------------------------------

  const handleSelectDispatchOrder = async (order) => {
    if (!order) return;

    try {
      setSelectedDispatchOrderId(String(order.id));
      setSelectedDispatchOrder(order);
      setDispatchItems([]);
      setError("");
      setInfo("");

      setLoadingItems(true);

      const response =
        await tradingActualDispatchService.getAvailableDispatchItems(
          order.id
        );

      const data = getResponseData(response);

      const rows = (data?.items || []).map((item) => ({
        ...item,
        actual_dispatch_quantity: "",
        warehouse_id: "",
        batch_allocations: [],
        available_batches: [],
        loading_batches: false,
        batch_load_error: "",
        showBatchAllocation: false,
      }));

      setSelectedDispatchOrder(data);
      setDispatchItems(rows);

      setInfo(
        rows.length
          ? "Available dispatch items loaded successfully."
          : "No dispatchable items are available for this order."
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoadingItems(false);
    }
  };

  // ---------------------------------------------------------
  // FILTER DISPATCH ORDERS
  // ---------------------------------------------------------

  const filteredDispatchOrders = useMemo(() => {
    const search = orderSearch.trim().toLowerCase();

    if (!search) return dispatchOrders;

    return dispatchOrders.filter((order) => {
      return (
        String(order.dispatch_order_no || "")
          .toLowerCase()
          .includes(search) ||
        String(order.sales_order_no || "")
          .toLowerCase()
          .includes(search) ||
        String(order.customer_name || "")
          .toLowerCase()
          .includes(search)
      );
    });
  }, [dispatchOrders, orderSearch]);

  // ---------------------------------------------------------
  // ITEM QUANTITY CHANGE
  // ---------------------------------------------------------

  const handleQuantityChange = (itemId, value) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          actual_dispatch_quantity: value,
          batch_allocations: [],
        };
      })
    );
  };

  // ---------------------------------------------------------
  // WAREHOUSE CHANGE
  // ---------------------------------------------------------

  
  
  const handleWarehouseChange_old11 = (itemId, value) => {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId)
          ? {
              ...item,
              warehouse_id: value,
            }
          : item
      )
    );
  };
  const handleWarehouseChange = async (itemId, value) => {
  setDispatchItems((previous) =>
    previous.map((item) =>
      String(item.dispatch_order_item_id) === String(itemId)
        ? {
            ...item,
            warehouse_id: value,
            batch_allocations: [],
            available_batches: [],
            loading_batches: Boolean(value),
            batch_load_error: "",
          }
        : item
    )
  );

  if (!value) return;

  const selectedItem = dispatchItems.find(
    (item) =>
      String(item.dispatch_order_item_id) === String(itemId)
  );

  if (!selectedItem?.raw_material_id) {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId)
          ? {
              ...item,
              loading_batches: false,
              batch_load_error: "Material ID is missing.",
            }
          : item
      )
    );
    return;
  }

  try {
    const response =
      await tradingActualDispatchService.getAvailableBatches(
        selectedItem.raw_material_id,
        value
      );

    const data = getResponseData(response);
    const batches = data?.items || [];

    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId) &&
        String(item.warehouse_id) === String(value)
          ? {
              ...item,
              available_batches: batches,
              loading_batches: false,
              batch_load_error: "",
            }
          : item
      )
    );
  } catch (err) {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId) &&
        String(item.warehouse_id) === String(value)
          ? {
              ...item,
              available_batches: [],
              loading_batches: false,
              batch_load_error: getErrorMessage(err),
            }
          : item
      )
    );
  }
};
  // ---------------------------------------------------------
  // BATCH ALLOCATION
  // ---------------------------------------------------------

  const toggleBatchAllocation = (itemId) => {
    setDispatchItems((previous) =>
      previous.map((item) =>
        String(item.dispatch_order_item_id) === String(itemId)
          ? {
              ...item,
              showBatchAllocation: !item.showBatchAllocation,
            }
          : item
      )
    );
  };

  const addBatchAllocation = (itemId) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          batch_allocations: [
            ...item.batch_allocations,
            {
              batch_no: "",
              dispatch_quantity: "",
            },
          ],
          showBatchAllocation: true,
        };
      })
    );
  };

  const updateBatchAllocation = (
  itemId,
  allocationIndex,
  field,
  value
) => {
  setDispatchItems((previous) =>
    previous.map((item) => {
      if (
        String(item.dispatch_order_item_id) !== String(itemId)
      ) {
        return item;
      }

      return {
        ...item,
        batch_allocations: item.batch_allocations.map(
          (allocation, index) => {
            if (index !== allocationIndex) {
              return allocation;
            }

            // Validate batch number against available batches.
            if (field === "batch_no") {
              const isValidBatch = item.available_batches?.some(
                (batch) => batch.batch_no === value
              );

              // Allow clearing the field while typing.
              if (value && !isValidBatch) {
                return {
                  ...allocation,
                  batch_no: value,
                };
              }
            }

            return {
              ...allocation,
              [field]: value,
            };
          }
        ),
      };
    })
  );
};

  const removeBatchAllocation = (itemId, allocationIndex) => {
    setDispatchItems((previous) =>
      previous.map((item) => {
        if (
          String(item.dispatch_order_item_id) !== String(itemId)
        ) {
          return item;
        }

        return {
          ...item,
          batch_allocations: item.batch_allocations.filter(
            (_, index) => index !== allocationIndex
          ),
        };
      })
    );
  };

  // ---------------------------------------------------------
  // TOTALS
  // ---------------------------------------------------------

  const totalDispatchQuantity = useMemo(
    () =>
      dispatchItems.reduce(
        (total, item) =>
          total + Number(item.actual_dispatch_quantity || 0),
        0
      ),
    [dispatchItems]
  );

  const selectedItemCount = dispatchItems.filter(
    (item) => Number(item.actual_dispatch_quantity || 0) > 0
  ).length;

  // ---------------------------------------------------------
  // VALIDATION AND SAVE
  // ---------------------------------------------------------

  const validateForm = () => {
    if (!selectedDispatchOrderId) {
      return "Please select an approved Dispatch Order.";
    }

    if (!actualDispatchDate) {
      return "Actual Dispatch Date is required.";
    }

    const selectedItems = dispatchItems.filter(
      (item) => Number(item.actual_dispatch_quantity || 0) > 0
    );

    if (!selectedItems.length) {
      return "Enter an actual dispatch quantity for at least one item.";
    }

    for (const item of selectedItems) {
      const quantity = Number(item.actual_dispatch_quantity);
      const remaining = Number(item.remaining_quantity || 0);

      if (!Number.isFinite(quantity) || quantity <= 0) {
        return `Enter a valid quantity for ${item.item_description}.`;
      }

      if (quantity > remaining) {
        return `Dispatch quantity for ${item.item_description} exceeds available quantity (${formatQuantity(remaining)}).`;
      }

      if (!item.warehouse_id) {
        return `Please select a warehouse for ${item.item_description}.`;
      }

      const allocations = item.batch_allocations || [];

      if (allocations.length) {
        let allocatedTotal = 0;
        const seenBatches = new Set();

        for (const allocation of allocations) {
          const batchNo = String(allocation.batch_no || "").trim();
          const batchQty = Number(allocation.dispatch_quantity);

          if (!batchNo) {
            return `Enter a batch number for ${item.item_description}.`;
          }

          if (!Number.isFinite(batchQty) || batchQty <= 0) {
            return `Enter a valid batch quantity for ${item.item_description}.`;
          }

          if (seenBatches.has(batchNo)) {
            return `Batch ${batchNo} is entered more than once for ${item.item_description}.`;
          }

          seenBatches.add(batchNo);
          allocatedTotal += batchQty;
        }

        if (Math.abs(allocatedTotal - quantity) > 0.000001) {
          return `Batch allocation total for ${item.item_description} must equal the actual dispatch quantity.`;
        }
      }
    }

    return "";
  };

  const handleSave = async () => {
    const validationMessage = validateForm();

    if (validationMessage) {
      setError(validationMessage);
      setInfo("");
      return;
    }

    const selectedItems = dispatchItems.filter(
      (item) => Number(item.actual_dispatch_quantity || 0) > 0
    );
    // Validate batch allocations against available inventory batches.
for (const item of selectedItems) {
  const actualQty = Number(item.actual_dispatch_quantity || 0);
  const allocations = item.batch_allocations || [];

  if (allocations.length === 0) {
    continue;
  }

  let totalAllocatedQty = 0;

  for (const allocation of allocations) {
    const batchNo = (allocation.batch_no || "").trim();
    const allocationQty = Number(allocation.dispatch_quantity || 0);

    if (!batchNo) {
      setError("Please select a batch number for all batch allocations.");
      setInfo("");
      return;
    }

    const selectedBatch = (item.available_batches || []).find(
      (batch) => batch.batch_no === batchNo
    );

    if (!selectedBatch) {
      setError(`Batch ${batchNo} is not available for the selected material and warehouse.`);
      setInfo("");
      return;
    }

    if (allocationQty <= 0) {
      setError(`Enter a valid quantity for batch ${batchNo}.`);
      setInfo("");
      return;
    }

    if (allocationQty > Number(selectedBatch.available_quantity || 0)) {
      setError(
        `Batch ${batchNo} has only ${formatQuantity(selectedBatch.available_quantity)} available.`
      );
      setInfo("");
      return;
    }

    totalAllocatedQty += allocationQty;
  }

  if (Math.abs(totalAllocatedQty - actualQty) > 0.001) {
    setError(
      `Total batch quantity (${formatQuantity(totalAllocatedQty)}) must equal Actual Dispatch quantity (${formatQuantity(actualQty)}).`
    );
    setInfo("");
    return;
  }
}
    const payload = {
      dispatch_order_id: Number(selectedDispatchOrderId),
      actual_dispatch_date: actualDispatchDate,
      transporter_bp_id: transporterBpId
        ? Number(transporterBpId)
        : null,
      vehicle_number: vehicleNumber.trim() || null,
      driver_name: driverName.trim() || null,
      driver_mobile: driverMobile.trim() || null,
      lr_number: lrNumber.trim() || null,
      lr_date: lrDate || null,
      remarks: remarks.trim() || null,

      items: selectedItems.map((item) => ({
        dispatch_order_item_id: Number(
          item.dispatch_order_item_id
        ),
        actual_dispatch_quantity: Number(
          item.actual_dispatch_quantity
        ),
        warehouse_id: Number(item.warehouse_id),

        batch_allocations: (item.batch_allocations || []).map(
          (allocation) => ({
            batch_no: allocation.batch_no.trim(),
            dispatch_quantity: Number(
              allocation.dispatch_quantity
            ),
          })
        ),
      })),
    };

    try {
      setSaving(true);
      setError("");
      setInfo("");

      const response =
        await tradingActualDispatchService.createActualDispatch(
          payload
        );

      const data = getResponseData(response);

      setInfo(
        `Actual Dispatch ${data.actual_dispatch_no || ""} created successfully.`
      );

      if (data.id) {
        navigate(`/trading/actual-dispatches/${data.id}`, {
          state: {
            created: true,
            actualDispatch: data,
          },
        });
      } else {
        navigate("/trading/actual-dispatches");
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------

  return (
    <div
      style={{
        padding: "24px",
        minHeight: "100%",
        background: "#f8fafc",
      }}
    >
      {/* PAGE HEADER */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          flexWrap: "wrap",
          marginBottom: "22px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              marginBottom: "6px",
            }}
          >
            Trading / Actual Dispatch / Create
          </div>

          <h2
            style={{
              margin: 0,
              fontSize: "24px",
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Create Actual Dispatch
          </h2>

          <p
            style={{
              margin: "7px 0 0",
              fontSize: "13px",
              color: "#64748b",
            }}
          >
            Record the actual shipment against an approved Dispatch Order.
          </p>
        </div>

        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate("/trading/actual-dispatches")}
          disabled={saving}
        >
          Back to Actual Dispatches
        </button>
      </div>

      {/* ALERTS */}

      {error && (
        <div
          role="alert"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#b91c1c",
            fontSize: "13px",
          }}
        >
          {error}
        </div>
      )}

      {info && (
        <div
          role="status"
          style={{
            padding: "12px 15px",
            borderRadius: "8px",
            marginBottom: "16px",
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1d4ed8",
            fontSize: "13px",
          }}
        >
          {info}
        </div>
      )}

      {/* SECTION 1: DISPATCH ORDER */}

      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          1. Select Approved Dispatch Order
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "minmax(260px, 420px) 1fr",
            gap: "16px",
            alignItems: "end",
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="dispatch-order-search">
              Search Dispatch Orders
            </label>

            <input
              id="dispatch-order-search"
              type="text"
              value={orderSearch}
              onChange={(event) => setOrderSearch(event.target.value)}
              placeholder="Search DO number, SO number or customer..."
              style={inputStyle}
            />
          </div>

          <div
            style={{
              fontSize: "12px",
              color: "#64748b",
              paddingBottom: "9px",
            }}
          >
            Only approved Dispatch Orders can be selected.
          </div>
        </div>

        {loadingOrders ? (
          <div style={{ padding: "22px", color: "#64748b" }}>
            Loading approved Dispatch Orders...
          </div>
        ) : filteredDispatchOrders.length === 0 ? (
          <div style={{ padding: "22px", color: "#64748b" }}>
            No approved Dispatch Orders found.
          </div>
        ) : (
          <div style={{ overflowX: "auto", marginTop: "16px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: "700px",
              }}
            >
              <thead>
                <tr>
                  <th style={tableHeaderStyle}>Select</th>
                  <th style={tableHeaderStyle}>Dispatch Order</th>
                  <th style={tableHeaderStyle}>Sales Order</th>
                  <th style={tableHeaderStyle}>Customer</th>
                  <th style={tableHeaderStyle}>Date</th>
                  <th style={tableHeaderStyle}>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredDispatchOrders.map((order) => (
                  <tr
                    key={order.id}
                    onClick={() => handleSelectDispatchOrder(order)}
                    style={{
                      cursor: "pointer",
                      background:
                        String(selectedDispatchOrderId) ===
                        String(order.id)
                          ? "#f5f3ff"
                          : "#fff",
                    }}
                  >
                    <td style={tableCellStyle}>
                      <input
                        type="radio"
                        name="selectedDispatchOrder"
                        checked={
                          String(selectedDispatchOrderId) ===
                          String(order.id)
                        }
                        onChange={() =>
                          handleSelectDispatchOrder(order)
                        }
                        onClick={(event) => event.stopPropagation()}
                        aria-label={`Select ${order.dispatch_order_no}`}
                      />
                    </td>

                    <td
                      style={{
                        ...tableCellStyle,
                        fontWeight: 700,
                        color: "#5145cd",
                      }}
                    >
                      {order.dispatch_order_no || `DO-${order.id}`}
                    </td>

                    <td style={tableCellStyle}>
                      {order.sales_order_no || "-"}
                    </td>

                    <td style={tableCellStyle}>
                      {order.customer_name || "-"}
                    </td>

                    <td style={tableCellStyle}>
                      {formatDate(
                        order.dispatch_order_date || order.created_at
                      )}
                    </td>

                    <td style={tableCellStyle}>
                      <span
                        style={{
                          padding: "4px 8px",
                          borderRadius: "5px",
                          background: "#dcfce7",
                          color: "#166534",
                          fontSize: "11px",
                          fontWeight: 700,
                        }}
                      >
                        APPROVED
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* SECTION 2: ORDER DETAILS */}

      {selectedDispatchOrder && (
        <section style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            2. Dispatch Order Details
          </h3>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "18px",
            }}
          >
            <div>
              <div style={labelStyle}>Dispatch Order Number</div>
              <strong>
                {selectedDispatchOrder.dispatch_order_no || "-"}
              </strong>
            </div>

            <div>
              <div style={labelStyle}>Sales Order Number</div>
              <strong>
                {selectedDispatchOrder.sales_order_no || "-"}
              </strong>
            </div>

            <div>
              <div style={labelStyle}>Customer</div>
              <strong>
                {selectedDispatchOrder.customer_name || "-"}
              </strong>
            </div>

            <div>
              <div style={labelStyle}>Customer ID</div>
              <strong>
                {selectedDispatchOrder.customer_id || "-"}
              </strong>
            </div>
          </div>

          <div style={{ marginTop: "18px" }}>
            <div style={labelStyle}>Delivery Address</div>
            <div style={{ fontSize: "13px", color: "#374151" }}>
              {selectedDispatchOrder.delivery_address || "-"}
            </div>
          </div>
        </section>
      )}

      {/* SECTION 3: ACTUAL DISPATCH ITEMS */}

      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          3. Actual Dispatch Items
        </h3>

        {!selectedDispatchOrderId ? (
          <div style={{ color: "#64748b", fontSize: "13px" }}>
            Select an approved Dispatch Order to load available items.
          </div>
        ) : loadingItems ? (
          <div style={{ padding: "20px", color: "#64748b" }}>
            Loading available dispatch items...
          </div>
        ) : dispatchItems.length === 0 ? (
          <div style={{ color: "#64748b", fontSize: "13px" }}>
            No dispatchable items are available for this Dispatch Order.
          </div>
        ) : (
          <>
            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  minWidth: "1050px",
                }}
              >
                <thead>
                  <tr>
                    <th style={tableHeaderStyle}>Line</th>
                    <th style={tableHeaderStyle}>Item Description</th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                      Planned Qty
                    </th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                      Confirmed Qty
                    </th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                      Draft Reserved
                    </th>
                    <th style={{ ...tableHeaderStyle, textAlign: "right" }}>
                      Available Qty
                    </th>
                    <th style={{ ...tableHeaderStyle, minWidth: "130px" }}>
                      Actual Dispatch Qty
                    </th>
                    <th style={{ ...tableHeaderStyle, minWidth: "180px" }}>
                      Warehouse
                    </th>
                    <th style={tableHeaderStyle}>Batch Allocation</th>
                  </tr>
                </thead>

                <tbody>
                  {dispatchItems.map((item) => {
                    const quantity = Number(
                      item.actual_dispatch_quantity || 0
                    );

                    const allocationTotal = (
                      item.batch_allocations || []
                    ).reduce(
                      (sum, allocation) =>
                        sum +
                        Number(allocation.dispatch_quantity || 0),
                      0
                    );

                    return (
                      <React.Fragment
                        key={item.dispatch_order_item_id}
                      >
                        <tr>
                          <td style={tableCellStyle}>
                            {item.item_seq_no ?? "-"}
                          </td>

                          <td style={tableCellStyle}>
                            <div style={{ fontWeight: 600 }}>
                              {item.item_description || "-"}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#64748b",
                                marginTop: "3px",
                              }}
                            >
                              Material ID: {item.raw_material_id || "-"}
                            </div>
                          </td>

                          <td
                            style={{
                              ...tableCellStyle,
                              textAlign: "right",
                            }}
                          >
                            {formatQuantity(
                              item.planned_dispatch_quantity
                            )}
                          </td>

                          <td
                            style={{
                              ...tableCellStyle,
                              textAlign: "right",
                            }}
                          >
                            {formatQuantity(
                              item.confirmed_dispatch_quantity
                            )}
                          </td>

                          <td
                            style={{
                              ...tableCellStyle,
                              textAlign: "right",
                            }}
                          >
                            {formatQuantity(
                              item.draft_reserved_quantity
                            )}
                          </td>

                          <td
                            style={{
                              ...tableCellStyle,
                              textAlign: "right",
                              fontWeight: 700,
                            }}
                          >
                            {formatQuantity(item.remaining_quantity)}
                          </td>

                          <td style={tableCellStyle}>
                            <input
                              type="number"
                              min="0"
                              max={Number(item.remaining_quantity || 0)}
                              step="0.001"
                              value={item.actual_dispatch_quantity}
                              onChange={(event) =>
                                handleQuantityChange(
                                  item.dispatch_order_item_id,
                                  event.target.value
                                )
                              }
                              disabled={
                                Number(item.remaining_quantity || 0) <= 0
                              }
                              style={inputStyle}
                            />
                          </td>

                          <td style={tableCellStyle}>
                            <select
                              value={item.warehouse_id}
                              onChange={(event) =>
                                handleWarehouseChange(
                                  item.dispatch_order_item_id,
                                  event.target.value
                                )
                              }
                              disabled={loadingWarehouses}
                              style={inputStyle}
                            >
                              <option value="">
                                {loadingWarehouses
                                  ? "Loading warehouses..."
                                  : "Select Warehouse"}
                              </option>

                              {warehouses.map((warehouse) => (
                                <option
                                  key={getWarehouseId(warehouse)}
                                  value={getWarehouseId(warehouse)}
                                >
                                  {getWarehouseName(warehouse)}
                                </option>
                              ))}
                            </select>
                          </td>

                          <td style={tableCellStyle}>
                            <button
                              type="button"
                              style={{
                                ...secondaryButtonStyle,
                                padding: "6px 9px",
                                fontSize: "11px",
                              }}
                              disabled={quantity <= 0}
                              onClick={() =>
                                toggleBatchAllocation(
                                  item.dispatch_order_item_id
                                )
                              }
                            >
                              {item.showBatchAllocation
                                ? "Hide Batches"
                                : `Batches (${item.batch_allocations.length})`}
                            </button>
                          </td>
                        </tr>

                        {item.showBatchAllocation && (
                          <tr>
                            <td
                              colSpan={9}
                              style={{
                                padding: "14px",
                                background: "#f8fafc",
                                borderBottom: "1px solid #e5e7eb",
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: "12px",
                                  flexWrap: "wrap",
                                  marginBottom: "12px",
                                }}
                              >
                                <div>
                                  <strong
                                    style={{
                                      fontSize: "13px",
                                      color: "#1f2937",
                                    }}
                                  >
                                    Batch Allocation
                                  </strong>

                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#64748b",
                                      marginTop: "4px",
                                    }}
                                  >
                                    Actual quantity: {formatQuantity(quantity)}
                                    {" | "}
                                    Allocated: {formatQuantity(allocationTotal)}
                                  </div>
                                </div>

                                <button
                                  type="button"
                                  style={{
                                    ...primaryButtonStyle,
                                    padding: "7px 12px",
                                    fontSize: "11px",
                                  }}
                                  onClick={() =>
                                    addBatchAllocation(
                                      item.dispatch_order_item_id
                                    )
                                  }
                                >
                                  + Add Batch
                                </button>
                              </div>

                              {item.batch_allocations.length === 0 ? (
                                <div
                                  style={{
                                    fontSize: "12px",
                                    color: "#64748b",
                                  }}
                                >
                                  No batch allocations entered. Batch
                                  allocation will be validated against
                                  Inventory Configuration when saving.
                                </div>
                              ) : (
                                <div style={{ overflowX: "auto" }}>
                                  <table
                                    style={{
                                      width: "100%",
                                      maxWidth: "700px",
                                      borderCollapse: "collapse",
                                    }}
                                  >
                                    <thead>
                                      <tr>
                                        <th style={tableHeaderStyle}>
                                          Batch Number
                                        </th>
                                        <th style={tableHeaderStyle}>
                                          Dispatch Quantity
                                        </th>
                                        <th style={tableHeaderStyle}>
                                          Action
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody>
                                      {item.batch_allocations.map(
                                        (allocation, index) => (
                                          <tr key={index}>
                                            <td style={tableCellStyle}>
                                              <>
                                                <input
                                                  type="text"
                                                  list={`batch-options-${item.dispatch_order_item_id}`}
                                                  value={allocation.batch_no}
                                                  placeholder={
                                                    !item.warehouse_id
                                                      ? "Select warehouse first"
                                                      : item.loading_batches
                                                      ? "Loading batches..."
                                                      : "Search or select batch"
                                                  }
                                                  disabled={
                                                    !item.warehouse_id ||
                                                    item.loading_batches ||
                                                    item.available_batches.length === 0
                                                  }
                                                  onChange={(event) =>
                                                    updateBatchAllocation(
                                                      item.dispatch_order_item_id,
                                                      index,
                                                      "batch_no",
                                                      event.target.value
                                                    )
                                                  }
                                                  style={{
                                                    ...inputStyle,
                                                    maxWidth: "260px",
                                                  }}
                                                />

                                                <datalist
                                                  id={`batch-options-${item.dispatch_order_item_id}`}
                                                >
                                                  {item.available_batches.map((batch) => (
                                                    <option
                                                      key={batch.batch_no}
                                                      value={batch.batch_no}
                                                      label={`Available: ${formatQuantity(
                                                        batch.available_quantity
                                                      )}`}
                                                    />
                                                  ))}
                                                </datalist>
                                                {allocation.batch_no && (
                                                  <div
                                                    style={{
                                                      fontSize: "12px",
                                                      color: "#198754",
                                                      marginTop: "4px",
                                                    }}
                                                  >
                                                    Available Qty:{" "}
                                                    {formatQuantity(
                                                      item.available_batches.find(
                                                        (batch) => batch.batch_no === allocation.batch_no
                                                      )?.available_quantity || 0
                                                    )}
                                                  </div>
                                                )}
                                              </>
                                            </td>

                                            <td style={tableCellStyle}>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.001"
                                                value={
                                                  allocation.dispatch_quantity
                                                }
                                                placeholder="Quantity"
                                                onChange={(event) =>
                                                  updateBatchAllocation(
                                                    item.dispatch_order_item_id,
                                                    index,
                                                    "dispatch_quantity",
                                                    event.target.value
                                                  )
                                                }
                                                style={{
                                                  ...inputStyle,
                                                  maxWidth: "180px",
                                                }}
                                              />
                                            </td>

                                            <td style={tableCellStyle}>
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  removeBatchAllocation(
                                                    item.dispatch_order_item_id,
                                                    index
                                                  )
                                                }
                                                style={{
                                                  ...secondaryButtonStyle,
                                                  color: "#b91c1c",
                                                  padding: "6px 10px",
                                                }}
                                              >
                                                Remove
                                              </button>
                                            </td>
                                          </tr>
                                        )
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}

                              <div
                                style={{
                                  marginTop: "10px",
                                  fontSize: "12px",
                                  fontWeight: 600,
                                  color:
                                    item.batch_allocations.length > 0 &&
                                    Math.abs(allocationTotal - quantity) >
                                      0.000001
                                      ? "#b91c1c"
                                      : "#166534",
                                }}
                              >
                                Allocated: {formatQuantity(allocationTotal)}
                                {" / "}
                                {formatQuantity(quantity)}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "24px",
                flexWrap: "wrap",
                marginTop: "16px",
                padding: "13px 15px",
                borderRadius: "7px",
                background: "#f8fafc",
                fontSize: "13px",
              }}
            >
              <div>
                <span style={{ color: "#64748b" }}>Selected Items: </span>
                <strong>{selectedItemCount}</strong>
              </div>

              <div>
                <span style={{ color: "#64748b" }}>
                  Total Actual Dispatch Quantity:{" "}
                </span>
                <strong>{formatQuantity(totalDispatchQuantity)}</strong>
              </div>
            </div>
          </>
        )}
      </section>

      {/* SECTION 4: ACTUAL DISPATCH DETAILS */}

      <section style={cardStyle}>
        <h3 style={sectionTitleStyle}>
          4. Actual Dispatch and Transport Details
        </h3>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "18px",
          }}
        >
          <div>
            <label style={labelStyle} htmlFor="actual-dispatch-date">
              Actual Dispatch Date *
            </label>

            <input
              id="actual-dispatch-date"
              type="date"
              value={actualDispatchDate}
              onChange={(event) =>
                setActualDispatchDate(event.target.value)
              }
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="actual-transporter">
              Transporter
            </label>

            <select
              id="actual-transporter"
              value={transporterBpId}
              onChange={(event) =>
                setTransporterBpId(event.target.value)
              }
              disabled={loadingTransporters}
              style={inputStyle}
            >
              <option value="">
                {loadingTransporters
                  ? "Loading transporters..."
                  : "Select Transporter"}
              </option>

              {transporters.map((transporter) => (
                <option
                  key={transporter.id}
                  value={transporter.id}
                >
                  {transporter.name || transporter.bp_name || transporter.code}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle} htmlFor="vehicle-number">
              Vehicle Number
            </label>

            <input
              id="vehicle-number"
              type="text"
              value={vehicleNumber}
              onChange={(event) =>
                setVehicleNumber(event.target.value)
              }
              style={inputStyle}
              placeholder="Enter vehicle number"
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="driver-name">
              Driver Name
            </label>

            <input
              id="driver-name"
              type="text"
              value={driverName}
              onChange={(event) => setDriverName(event.target.value)}
              style={inputStyle}
              placeholder="Enter driver name"
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="driver-mobile">
              Driver Mobile
            </label>

            <input
              id="driver-mobile"
              type="text"
              value={driverMobile}
              onChange={(event) =>
                setDriverMobile(event.target.value)
              }
              style={inputStyle}
              placeholder="Enter driver mobile"
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="lr-number">
              LR / Docket Number
            </label>

            <input
              id="lr-number"
              type="text"
              value={lrNumber}
              onChange={(event) => setLrNumber(event.target.value)}
              style={inputStyle}
              placeholder="Enter LR / docket number"
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="lr-date">
              LR Date
            </label>

            <input
              id="lr-date"
              type="date"
              value={lrDate}
              onChange={(event) => setLrDate(event.target.value)}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginTop: "18px" }}>
          <label style={labelStyle} htmlFor="actual-dispatch-remarks">
            Remarks
          </label>

          <textarea
            id="actual-dispatch-remarks"
            rows={3}
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            style={{
              ...inputStyle,
              resize: "vertical",
            }}
            placeholder="Enter dispatch remarks"
          />
        </div>
      </section>

      {/* ACTIONS */}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: "10px",
          flexWrap: "wrap",
          paddingBottom: "24px",
        }}
      >
        <button
          type="button"
          style={secondaryButtonStyle}
          onClick={() => navigate("/trading/actual-dispatches")}
          disabled={saving}
        >
          Cancel
        </button>

        <button
          type="button"
          style={{
            ...primaryButtonStyle,
            opacity: saving ? 0.7 : 1,
            cursor: saving ? "not-allowed" : "pointer",
          }}
          onClick={handleSave}
          disabled={
            saving ||
            loadingItems ||
            loadingWarehouses ||
            !selectedDispatchOrderId
          }
        >
          {saving ? "Saving..." : "Save Actual Dispatch Draft"}
        </button>
      </div>
    </div>
  );
};

export default ActualDispatchCreatePage;